import * as THREE from 'three';
import { box, concatGeometries, cylinderN, mergeParts, part, taperedCylinder, type Part } from '@/art/geometry';
import { emissive, vertexColored } from '@/art/materials';

export const HARBOR_PROPS = {
  crateStack: {
    width: 1.5,
    height: 1.2,
    depth: 1.13,
    plankThickness: 0.08,
    triBudget: 170,
  },
  bollard: {
    radius: 0.28,
    height: 0.62,
    capHeight: 0.16,
    sides: 16,
    triBudget: 150,
  },
  crane: {
    width: 3.8,
    height: 7.4,
    depth: 2.4,
    boomLength: 7.2,
    triBudget: 1100,
  },
  neonSign: {
    width: 2.4,
    height: 1.05,
    depth: 0.18,
    glowHeight: 2.7,
    triBudget: 120,
  },
  phoneBooth: {
    width: 0.95,
    height: 2.25,
    depth: 0.86,
    triBudget: 520,
  },
  bench: {
    width: 1.9,
    height: 1.02,
    depth: 0.72,
    triBudget: 260,
  },
  hydrant: {
    radius: 0.18,
    height: 0.78,
    sides: 16,
    triBudget: 560,
  },
  trafficLight: {
    width: 0.78,
    height: 3.3,
    depth: 0.92,
    triBudget: 430,
  },
  boat: {
    width: 4.45,
    length: 13.4,
    height: 3.75,
    draft: 0.62,
    triBudget: 5200,
  },
} as const;

export type HarborPropKind =
  | 'crateStack'
  | 'bollard'
  | 'crane'
  | 'neonSign'
  | 'phoneBooth'
  | 'bench'
  | 'hydrant'
  | 'trafficLight'
  | 'boat';

export function buildHarborProp(kind: HarborPropKind): THREE.Group {
  switch (kind) {
    case 'crateStack':
      return buildCrateStack();
    case 'bollard':
      return buildBollard();
    case 'crane':
      return buildHarborCrane();
    case 'neonSign':
      return buildNeonSign();
    case 'phoneBooth':
      return buildPhoneBooth();
    case 'bench':
      return buildBench();
    case 'hydrant':
      return buildHydrant();
    case 'trafficLight':
      return buildTrafficLight();
    case 'boat':
      return buildHarborBoat();
  }
}

export function buildCrateStack(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'NA_Prop_CrateStack';

  const c = HARBOR_PROPS.crateStack;
  const parts: Part[] = [];
  const crates = [
    { x: -0.28, y: 0.31, z: -0.14, w: 0.78, h: 0.62, d: 0.78 },
    { x: 0.42, y: 0.27, z: 0.18, w: 0.72, h: 0.54, d: 0.72 },
    { x: -0.02, y: 0.9, z: 0.05, w: 0.82, h: 0.58, d: 0.72 },
  ];

  for (const crate of crates) {
    parts.push(part(box(crate.w, crate.h, crate.d).translate(crate.x, crate.y, crate.z), 'weatheredWood'));
    parts.push(
      part(
        box(crate.w + 0.04, c.plankThickness, crate.d + 0.05).translate(
          crate.x,
          crate.y + crate.h * 0.18,
          crate.z,
        ),
        'nightBrick',
      ),
    );
    parts.push(
      part(
        box(c.plankThickness, crate.h + 0.04, crate.d + 0.05).translate(
          crate.x - crate.w * 0.28,
          Math.max(crate.y, crate.h / 2 + 0.02),
          crate.z,
        ),
        'nightBrick',
      ),
    );
  }

  const mesh = new THREE.Mesh(mergeParts(parts), vertexColored());
  mesh.name = 'shell';
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  group.add(mesh);
  return group;
}

export function buildBollard(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'NA_Prop_Bollard';
  const b = HARBOR_PROPS.bollard;

  const parts: Part[] = [
    part(
      taperedCylinder(b.radius * 0.72, b.radius, b.height - b.capHeight, b.sides).translate(
        0,
        (b.height - b.capHeight) / 2,
        0,
      ),
      'darkSteel',
    ),
    part(
      cylinderN(b.radius * 1.04, b.capHeight, b.sides).translate(0, b.height - b.capHeight / 2, 0),
      'slateGrey',
    ),
    part(box(b.radius * 2.25, 0.1, b.radius * 0.38).translate(0, b.height * 0.48, 0), 'nightStone'),
  ];

  const mesh = new THREE.Mesh(mergeParts(parts), vertexColored());
  mesh.name = 'shell';
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  group.add(mesh);
  return group;
}

export function buildHarborCrane(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'NA_Prop_HarborCrane';
  const c = HARBOR_PROPS.crane;

  const parts: Part[] = [
    part(box(2.15, 0.38, 1.85).translate(0, 0.19, 0), 'nightStone'),
    part(box(1.6, 0.18, 1.28).translate(0, 0.48, 0), 'darkSteel'),
    part(box(0.28, c.height - 0.4, 0.28).translate(-0.45, c.height / 2 + 0.2, -0.42), 'darkSteel'),
    part(box(0.28, c.height - 0.4, 0.28).translate(0.45, c.height / 2 + 0.2, -0.42), 'darkSteel'),
    part(box(0.28, c.height - 0.4, 0.28).translate(-0.45, c.height / 2 + 0.2, 0.42), 'darkSteel'),
    part(box(0.28, c.height - 0.4, 0.28).translate(0.45, c.height / 2 + 0.2, 0.42), 'darkSteel'),
    part(box(1.45, 0.92, 1.1).translate(0.8, c.height - 0.58, 0), 'rustOrange'),
    part(box(0.78, 0.54, 0.68).translate(0.15, c.height - 0.36, 0.04), 'windowCold'),
    part(box(c.boomLength, 0.18, 0.22).rotateZ(0.13).translate(c.boomLength / 2 - 0.45, c.height + 0.72, 0), 'rustOrange'),
    part(box(c.boomLength * 0.92, 0.12, 0.16).rotateZ(-0.1).translate(c.boomLength / 2 - 0.18, c.height + 0.34, 0), 'rustOrange'),
    part(box(1.85, 0.42, 0.56).translate(-1.28, c.height + 0.38, 0), 'nightStone'),
    part(box(0.09, 1.72, 0.09).translate(c.boomLength - 0.42, c.height - 0.18, 0), 'darkSteel'),
    part(box(0.62, 0.2, 0.22).translate(c.boomLength - 0.42, c.height - 1.08, 0), 'darkSteel'),
    part(box(0.2, 0.6, 0.2).translate(c.boomLength - 0.22, c.height - 1.38, 0), 'darkSteel'),
  ];

  for (const y of [1.8, 3.3, 4.8, 6.2]) {
    parts.push(
      part(box(1.15, 0.09, 0.09).translate(0, y, -0.42), 'darkSteel'),
      part(box(1.15, 0.09, 0.09).translate(0, y, 0.42), 'darkSteel'),
      part(box(0.09, 0.09, 1.12).translate(-0.45, y + 0.12, 0), 'darkSteel'),
      part(box(0.09, 0.09, 1.12).translate(0.45, y - 0.12, 0), 'darkSteel'),
      part(box(1.42, 0.08, 0.08).rotateZ(0.58).translate(0, y - 0.44, -0.42), 'darkSteel'),
      part(box(1.42, 0.08, 0.08).rotateZ(-0.58).translate(0, y - 0.44, 0.42), 'darkSteel'),
    );
  }

  for (let i = 0; i < 5; i++) {
    parts.push(
      part(
        box(0.08, 0.08, 1.05).rotateZ(i % 2 === 0 ? 0.52 : -0.52)
          .translate(1.2 + i * 1.1, c.height + 0.48 + i * 0.12, 0),
        'darkSteel',
      ),
    );
  }

  const mesh = new THREE.Mesh(mergeParts(parts), vertexColored());
  mesh.name = 'shell';
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  group.add(mesh);
  return group;
}

export function buildNeonSign(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'NA_Prop_NeonSign';
  const s = HARBOR_PROPS.neonSign;

  const shellParts: Part[] = [
    part(box(s.width, s.height, s.depth), 'nightBrick'),
    part(box(s.width + 0.16, 0.08, s.depth + 0.04).translate(0, s.height / 2 + 0.04, 0), 'darkSteel'),
    part(box(s.width + 0.16, 0.08, s.depth + 0.04).translate(0, -s.height / 2 - 0.04, 0), 'darkSteel'),
  ];

  const shell = new THREE.Mesh(mergeParts(shellParts), vertexColored());
  shell.name = 'shell';
  shell.position.y = s.glowHeight;
  shell.castShadow = true;
  shell.receiveShadow = true;
  group.add(shell);

  const glowParts: Part[] = [
    part(box(0.12, 0.74, 0.05).translate(-0.72, 0, 0), 'neonRed'),
    part(box(0.58, 0.12, 0.05).translate(-0.43, 0.31, 0), 'neonRed'),
    part(box(0.58, 0.12, 0.05).translate(-0.43, 0, 0), 'neonRed'),
    part(box(0.12, 0.74, 0.05).translate(0.05, 0, 0), 'neonBlue'),
    part(box(0.5, 0.12, 0.05).translate(0.3, 0.31, 0), 'neonBlue'),
    part(box(0.5, 0.12, 0.05).translate(0.3, -0.31, 0), 'neonBlue'),
    part(box(0.12, 0.74, 0.05).translate(0.55, 0, 0), 'neonBlue'),
  ];
  const glow = new THREE.Mesh(mergeParts(glowParts), emissive('neonRed', 1.15));
  glow.name = 'glow';
  glow.position.set(0, s.glowHeight, s.depth / 2 + 0.04);
  group.add(glow);

  return group;
}

export function neonSignLightOffset(): THREE.Vector3 {
  return new THREE.Vector3(0, HARBOR_PROPS.neonSign.glowHeight, 0.75);
}

export function buildPhoneBooth(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'NA_Prop_PhoneBooth';
  const p = HARBOR_PROPS.phoneBooth;
  const halfWidth = p.width / 2;
  const halfDepth = p.depth / 2;

  const parts: Part[] = [
    part(box(p.width, 0.1, p.depth).translate(0, 0.05, 0), 'darkSteel'),
    part(box(p.width * 1.08, 0.16, p.depth * 1.08).translate(0, p.height - 0.08, 0), 'darkSteel'),
    part(box(0.12, p.height, 0.12).translate(-halfWidth + 0.06, p.height / 2, -halfDepth + 0.06), 'neonBlue'),
    part(box(0.12, p.height, 0.12).translate(halfWidth - 0.06, p.height / 2, -halfDepth + 0.06), 'neonBlue'),
    part(box(0.12, p.height, 0.12).translate(-halfWidth + 0.06, p.height / 2, halfDepth - 0.06), 'neonBlue'),
    part(box(0.12, p.height, 0.12).translate(halfWidth - 0.06, p.height / 2, halfDepth - 0.06), 'neonBlue'),
    part(box(p.width * 0.74, 0.22, 0.08).translate(0, 1.96, halfDepth + 0.01), 'enamelWhite'),
    part(box(p.width * 0.54, 0.06, 0.03).translate(0, 2.02, halfDepth + 0.015), 'rubberBlack'),
    part(box(p.width * 0.72, 1.24, 0.03).translate(0, 1.18, halfDepth + 0.015), 'windowCold'),
    part(box(0.05, 1.16, 0.03).translate(0, 1.16, halfDepth + 0.015), 'darkSteel'),
    part(box(p.width * 0.64, 1.12, 0.04).rotateY(Math.PI / 2).translate(-halfWidth - 0.025, 1.22, 0), 'windowCold'),
    part(box(p.width * 0.64, 1.12, 0.04).rotateY(Math.PI / 2).translate(halfWidth + 0.025, 1.22, 0), 'windowCold'),
    part(box(0.42, 0.48, 0.04).translate(0, 0.72, halfDepth + 0.02), 'darkSteel'),
    part(cylinderN(0.055, 0.03, 12).rotateX(Math.PI / 2).translate(0.2, 0.86, halfDepth + 0.015), 'rubberBlack'),
  ];

  const mesh = new THREE.Mesh(mergeParts(parts), vertexColored());
  mesh.name = 'shell';
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  group.add(mesh);
  return group;
}

export function buildBench(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'NA_Prop_Bench';
  const b = HARBOR_PROPS.bench;
  const parts: Part[] = [
    part(box(b.width, 0.12, 0.34).translate(0, 0.52, -0.04), 'weatheredWood'),
    part(box(b.width, 0.12, 0.24).rotateX(-0.24).translate(0, 0.86, -0.3), 'weatheredWood'),
    part(box(b.width, 0.08, 0.08).translate(0, 0.64, 0.16), 'weatheredWood'),
    part(box(b.width * 0.96, 0.08, 0.08).translate(0, 0.98, -0.43), 'weatheredWood'),
    part(box(0.1, 0.5, 0.1).translate(-0.68, 0.25, 0.08), 'darkSteel'),
    part(box(0.1, 0.5, 0.1).translate(0.68, 0.25, 0.08), 'darkSteel'),
    part(box(0.1, 0.72, 0.1).rotateX(-0.18).translate(-0.68, 0.54, -0.35), 'darkSteel'),
    part(box(0.1, 0.72, 0.1).rotateX(-0.18).translate(0.68, 0.54, -0.35), 'darkSteel'),
  ];

  const mesh = new THREE.Mesh(mergeParts(parts), vertexColored());
  mesh.name = 'shell';
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  group.add(mesh);
  return group;
}

export function buildHydrant(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'NA_Prop_Hydrant';
  const h = HARBOR_PROPS.hydrant;
  const parts: Part[] = [
    part(cylinderN(h.radius * 1.18, 0.12, h.sides).translate(0, 0.06, 0), 'darkSteel'),
    part(cylinderN(h.radius, h.height * 0.62, h.sides).translate(0, 0.38, 0), 'neonCrimson'),
    part(taperedCylinder(h.radius * 0.72, h.radius, 0.18, h.sides).translate(0, 0.76, 0), 'neonCrimson'),
    part(cylinderN(h.radius * 0.52, 0.22, h.sides).rotateZ(Math.PI / 2).translate(-0.22, 0.45, 0), 'neonCrimson'),
    part(cylinderN(h.radius * 0.52, 0.22, h.sides).rotateZ(Math.PI / 2).translate(0.22, 0.45, 0), 'neonCrimson'),
    part(cylinderN(h.radius * 0.34, 0.08, h.sides).rotateZ(Math.PI / 2).translate(-0.36, 0.45, 0), 'darkSteel'),
    part(cylinderN(h.radius * 0.34, 0.08, h.sides).rotateZ(Math.PI / 2).translate(0.36, 0.45, 0), 'darkSteel'),
    part(cylinderN(h.radius * 0.5, 0.06, h.sides).translate(0, h.height + 0.03, 0), 'darkSteel'),
  ];

  const mesh = new THREE.Mesh(mergeParts(parts), vertexColored());
  mesh.name = 'shell';
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  group.add(mesh);
  return group;
}

export function buildTrafficLight(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'NA_Prop_TrafficLight';
  const t = HARBOR_PROPS.trafficLight;
  const parts: Part[] = [
    part(cylinderN(0.09, t.height, 12).translate(0, t.height / 2, 0), 'darkSteel'),
    part(box(0.38, 0.88, 0.26).translate(0.46, 2.42, 0), 'rubberBlack'),
    part(box(0.18, 0.06, 0.44).translate(0.24, 2.82, 0), 'darkSteel'),
    part(cylinderN(0.09, 0.06, 16).rotateX(Math.PI / 2).translate(0.46, 2.68, 0.16), 'neonRed'),
    part(cylinderN(0.09, 0.06, 16).rotateX(Math.PI / 2).translate(0.46, 2.42, 0.16), 'sodiumAmber'),
    part(cylinderN(0.09, 0.06, 16).rotateX(Math.PI / 2).translate(0.46, 2.16, 0.16), 'forestGreen'),
    part(box(1.12, 0.1, 0.1).translate(0.46, 3.08, 0), 'darkSteel'),
    part(box(0.4, 0.16, 0.16).translate(0.95, 3.08, 0), 'neonCrimson'),
    part(box(0.24, 0.06, 0.32).translate(0.95, 3.18, 0), 'enamelWhite'),
  ];

  const mesh = new THREE.Mesh(mergeParts(parts), vertexColored());
  mesh.name = 'shell';
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  group.add(mesh);
  return group;
}

export function buildHarborBoat(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'NA_Prop_HarborBoat';
  const b = HARBOR_PROPS.boat;
  const halfLength = b.length / 2;

  const parts: Part[] = [
    part(boatHullUpper(b.width, b.length), 'harborNavy'),
    part(boatHullLower(b.width, b.length, b.draft), 'hullRed'),
    part(boatDeck(b.width * 0.8, b.length * 0.62).translate(0, 1.04, -1.0), 'weatheredWood'),
    part(boatDeck(b.width * 0.98, b.length * 0.92).translate(0, 0.86, -0.12), 'harborNavy'),
    part(boatRubRail(b.width, b.length, 0.9, 0.16), 'rubberBlack'),
    part(boatRubRail(b.width * 0.99, b.length * 0.88, 1.18, 0.14), 'slateGrey'),
    part(boatRubRail(b.width * 0.95, b.length * 0.88, 0.12, 0.09), 'hullRed'),
    part(box(b.width * 0.64, 1.06, 4.25).translate(0, 1.68, -1.55), 'slateGrey'),
    part(box(b.width * 0.7, 0.2, 4.52).translate(0, 2.3, -1.55), 'weatheredWood'),
    part(box(b.width * 0.78, 0.16, 4.75).translate(0, 2.48, -1.55), 'darkSteel'),
    part(box(b.width * 0.68, 0.1, 4.36).translate(0, 2.61, -1.55), 'charcoal'),
    part(box(0.9, 0.08, 0.66).translate(-0.5, 2.68, -0.35), 'slateGrey'),
    part(box(0.62, 0.08, 0.5).translate(0.58, 2.67, -2.52), 'slateGrey'),
    part(box(0.1, 0.5, 3.52).translate(-1.42, 1.7, -1.55), 'windowCold'),
    part(box(0.1, 0.5, 3.52).translate(1.42, 1.7, -1.55), 'windowCold'),
    part(box(1.62, 0.44, 0.1).translate(0, 1.72, 0.88), 'windowCold'),
    part(box(1.25, 0.36, 0.1).translate(0, 1.58, -3.78), 'windowCold'),
    part(box(0.1, 0.56, 3.82).translate(-1.1, 1.7, -1.55), 'darkSteel'),
    part(box(0.1, 0.56, 3.82).translate(1.1, 1.7, -1.55), 'darkSteel'),
    part(box(1.68, 0.08, 0.08).translate(0, 1.98, 0.88), 'darkSteel'),
    part(box(0.14, 2.05, 0.14).translate(0.08, 2.05, halfLength - 2.72), 'weatheredWood'),
    part(box(1.58, 0.09, 0.09).translate(0.08, 3.0, halfLength - 2.72), 'darkSteel'),
    part(box(0.08, 0.74, 0.08).translate(-0.42, 3.18, halfLength - 2.72), 'darkSteel'),
    part(box(0.68, 0.06, 0.06).translate(-0.08, 3.45, halfLength - 2.72), 'darkSteel'),
    part(box(0.3, 0.16, 0.08).translate(-0.58, 1.08, halfLength - 1.18), 'headlight'),
    part(box(0.3, 0.16, 0.08).translate(0.58, 1.08, halfLength - 1.18), 'headlight'),
    part(box(0.16, 0.18, 0.06).translate(-0.82, 0.82, -halfLength + 0.72), 'tailLight'),
    part(box(0.16, 0.18, 0.06).translate(0.82, 0.82, -halfLength + 0.72), 'tailLight'),
  ];

  for (const side of [-1, 1]) {
    const x = side * (b.width / 2 - 0.28);
    parts.push(
      part(box(0.08, 0.56, b.length * 0.5).translate(x, 1.34, -0.68), 'darkSteel'),
      part(box(0.08, 0.08, b.length * 0.62).translate(x, 1.62, -0.68), 'darkSteel'),
      part(box(0.18, 0.34, 0.44).translate(x, 0.58, 2.35), 'slateGrey'),
      part(box(0.18, 0.34, 0.44).translate(x, 0.58, -3.78), 'slateGrey'),
      part(box(0.08, 0.48, 0.08).translate(x, 1.28, halfLength - 3.28), 'darkSteel'),
      part(box(0.08, 0.48, 0.08).translate(x, 1.28, halfLength - 4.08), 'darkSteel'),
      part(box(0.08, 0.48, 0.08).translate(x, 1.28, -halfLength + 1.72), 'darkSteel'),
    );
  }

  for (const z of [-3.46, -2.42, -1.38, -0.12, 1.2, 2.52]) {
    parts.push(
      part(cylinderN(0.18, 0.055, 16).rotateZ(Math.PI / 2).translate(-b.width / 2 + 0.1, 0.72, z), 'enamelWhite'),
      part(cylinderN(0.18, 0.055, 16).rotateZ(Math.PI / 2).translate(b.width / 2 - 0.1, 0.72, z), 'enamelWhite'),
      part(cylinderN(0.11, 0.06, 16).rotateZ(Math.PI / 2).translate(-b.width / 2 + 0.09, 0.72, z), 'windowCold'),
      part(cylinderN(0.11, 0.06, 16).rotateZ(Math.PI / 2).translate(b.width / 2 - 0.09, 0.72, z), 'windowCold'),
    );
  }

  for (const z of [-4.8, -3.2, -1.2, 0.9, 2.9]) {
    parts.push(
      part(cylinderN(0.12, 0.22, 16).translate(-b.width / 2 + 0.56, 0.96, z), 'darkSteel'),
      part(cylinderN(0.12, 0.22, 16).translate(b.width / 2 - 0.56, 0.96, z), 'darkSteel'),
    );
  }

  const mesh = new THREE.Mesh(mergeParts(parts), vertexColored());
  mesh.name = 'shell';
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  group.add(mesh);

  const wake = new THREE.Mesh(
    mergeParts([
      part(box(1.16, 0.01, 0.055).translate(-0.74, 0.018, halfLength - 0.64), 'canalHighlight'),
      part(box(1.16, 0.01, 0.055).translate(0.74, 0.018, halfLength - 0.64), 'canalHighlight'),
      part(box(2.1, 0.012, 0.06).translate(0, 0.018, halfLength - 1.08), 'canalHighlight'),
      part(box(2.45, 0.01, 0.055).translate(0, 0.016, -halfLength + 0.36), 'canalHighlight'),
      part(box(1.2, 0.01, 0.045).translate(-1.16, 0.018, -halfLength + 0.92), 'canalHighlight'),
      part(box(1.2, 0.01, 0.045).translate(1.16, 0.018, -halfLength + 0.92), 'canalHighlight'),
    ]),
    emissive('canalHighlight', 0.46),
  );
  wake.name = 'wake';
  wake.rotation.y = 0.08;
  group.add(wake);
  return group;
}

function boatRubRail(width: number, length: number, y: number, railWidth: number): THREE.BufferGeometry {
  const stations = boatStations(width, length);
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < stations.length - 1; i++) {
    const a = stations[i]!;
    const b = stations[i + 1]!;
    const z = (a.z + b.z) / 2;
    const segmentLength = Math.abs(b.z - a.z);
    const half = (a.top + b.top) / 2;
    parts.push(box(railWidth, 0.08, segmentLength + 0.06).translate(-half + railWidth * 0.5, y, z));
    parts.push(box(railWidth, 0.08, segmentLength + 0.06).translate(half - railWidth * 0.5, y, z));
  }
  parts.push(box(width * 0.56, 0.08, railWidth).translate(0, y, -length / 2 + 0.42));
  parts.push(box(width * 0.32, 0.08, railWidth).translate(0, y, length / 2 - 0.82));
  return concatGeometries(parts);
}

function boatHullUpper(width: number, length: number): THREE.BufferGeometry {
  const rings = boatStations(width, length).map((station) => ({
    topL: [-station.top, 0.86, station.z],
    topR: [station.top, 0.86, station.z],
    waterL: [-station.water, 0, station.z],
    waterR: [station.water, 0, station.z],
  }));

  const triangles: number[][] = [];
  for (let i = 0; i < rings.length - 1; i++) {
    const a = rings[i]!;
    const b = rings[i + 1]!;
    triangles.push(
      ...quad(a.topL, b.topL, b.waterL, a.waterL),
      ...quad(b.topR, a.topR, a.waterR, b.waterR),
      ...quad(a.topR, b.topR, b.topL, a.topL),
    );
  }

  const stern = rings[0]!;
  const bow = rings[rings.length - 1]!;
  triangles.push(
    ...quad(stern.topR, stern.topL, stern.waterL, stern.waterR),
    ...quad(bow.topL, bow.topR, bow.waterR, bow.waterL),
  );
  return fromTriangles(triangles);
}

function boatHullLower(width: number, length: number, draft: number): THREE.BufferGeometry {
  const rings = boatStations(width, length).map((station) => ({
    waterL: [-station.water, 0, station.z],
    waterR: [station.water, 0, station.z],
    chineL: [-station.chine, -draft * 0.42, station.z],
    chineR: [station.chine, -draft * 0.42, station.z],
    keelL: [-station.keel, -draft, station.z],
    keelR: [station.keel, -draft, station.z],
  }));

  const triangles: number[][] = [];
  for (let i = 0; i < rings.length - 1; i++) {
    const a = rings[i]!;
    const b = rings[i + 1]!;
    triangles.push(
      ...quad(a.waterL, b.waterL, b.chineL, a.chineL),
      ...quad(b.waterR, a.waterR, a.chineR, b.chineR),
      ...quad(a.chineL, b.chineL, b.keelL, a.keelL),
      ...quad(b.chineR, a.chineR, a.keelR, b.keelR),
      ...quad(a.keelL, b.keelL, b.keelR, a.keelR),
    );
  }

  const stern = rings[0]!;
  const bow = rings[rings.length - 1]!;
  triangles.push(
    ...quad(stern.waterR, stern.waterL, stern.chineL, stern.chineR),
    ...quad(stern.chineR, stern.chineL, stern.keelL, stern.keelR),
    ...quad(bow.waterL, bow.waterR, bow.chineR, bow.chineL),
    [...bow.chineL, ...bow.chineR, ...bow.keelR],
    [...bow.chineL, ...bow.keelR, ...bow.keelL],
  );

  const positions = new Float32Array(triangles.flat());
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  return geometry;
}

function boatStations(width: number, length: number): Array<{ z: number; top: number; water: number; chine: number; keel: number }> {
  const halfLength = length / 2;
  return [
    { z: -halfLength + 0.18, top: width * 0.34, water: width * 0.3, chine: width * 0.2, keel: width * 0.1 },
    { z: -halfLength + 0.92, top: width * 0.46, water: width * 0.41, chine: width * 0.27, keel: width * 0.13 },
    { z: -halfLength + 1.84, top: width * 0.52, water: width * 0.46, chine: width * 0.31, keel: width * 0.15 },
    { z: -halfLength * 0.36, top: width * 0.5, water: width * 0.45, chine: width * 0.3, keel: width * 0.14 },
    { z: 0.2, top: width * 0.49, water: width * 0.44, chine: width * 0.29, keel: width * 0.13 },
    { z: halfLength * 0.38, top: width * 0.44, water: width * 0.39, chine: width * 0.25, keel: width * 0.11 },
    { z: halfLength - 1.88, top: width * 0.36, water: width * 0.3, chine: width * 0.19, keel: width * 0.09 },
    { z: halfLength - 0.9, top: width * 0.28, water: width * 0.22, chine: width * 0.14, keel: width * 0.06 },
    { z: halfLength - 0.28, top: width * 0.18, water: width * 0.13, chine: width * 0.08, keel: width * 0.035 },
  ];
}

function boatDeck(width: number, length: number): THREE.BufferGeometry {
  const halfLength = length / 2;
  const y = 0;
  const stations = [
    { z: -halfLength + 0.24, half: width * 0.42 },
    { z: -halfLength * 0.55, half: width * 0.5 },
    { z: halfLength * 0.1, half: width * 0.48 },
    { z: halfLength * 0.55, half: width * 0.34 },
    { z: halfLength - 0.84, half: width * 0.18 },
  ];
  const triangles: number[][] = [];
  for (let i = 0; i < stations.length - 1; i++) {
    const a = stations[i]!;
    const b = stations[i + 1]!;
    triangles.push(...quad([-a.half, y, a.z], [a.half, y, a.z], [b.half, y, b.z], [-b.half, y, b.z]));
  }
  return fromTriangles(triangles);
}

function fromTriangles(triangles: number[][]): THREE.BufferGeometry {
  const positions = new Float32Array(triangles.flat());
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  return geometry;
}


function quad(a: number[], b: number[], c: number[], d: number[]): number[][] {
  return [
    [...a, ...b, ...c],
    [...a, ...c, ...d],
  ];
}
